import React from 'react';
import { Trans, useBi, useTranslation } from '@wix/yoshi-flow-editor';
import {
  TextButton,
  TextButtonPriority,
  ThreeDotsLoader,
} from '@wix/wix-ui-tpa/cssVars';
import { issueCertificate } from '@wix/ambassador-challenges-v1-participant/http';
import { memberWebAppButtonClick as memberWebAppButtonClickV2 } from '@wix/bi-logger-challenges-member-web/v2';
import { useToast } from '../../contexts/ToastContext/ToastContext';
import { ToastType } from '../../contexts/ToastContext/interfaces';
import { classes } from './ViewCertificate.st.css';
import { ButtonNames } from '../../contexts/main/biInterfaces';
import { FCWithChildren } from '@wix/challenges-web-library';
import { useUser } from '../../contexts/User/UserContext';
import { useChallengeData } from '../../contexts/storage-contexts/Challenge';
import { usePromisifyAmbassadorRequest } from '../../services/asyncController/usePromisifyAmbassadorRequest';

export interface IViewCertificateProps {
  certificateId: string;
  programId?: string;
}

export const ViewCertificate: FCWithChildren<IViewCertificateProps> = ({
  certificateId,
  programId,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { challengeData } = useChallengeData();
  const { user, requestMemberEmail } = useUser();
  const bi = useBi();
  const issueCert = usePromisifyAmbassadorRequest(
    issueCertificate,
    'issueCertificate',
  );

  React.useEffect(() => {
    requestMemberEmail();
  }, [requestMemberEmail]);

  const onClick = React.useCallback(async () => {
    if (!certificateId) {
      return;
    }

    bi.report(
      memberWebAppButtonClickV2({
        buttonName: ButtonNames.GetCertificate,
      }),
    );

    setLoading(true);

    try {
      await issueCert({
        participantId: certificateId,
        challengeId: challengeData?.challenge?.id || programId,
      });
      showToast(
        <Trans
          i18nKey="live.challenges-page.modal.challenge-completed.reward.certificate"
          components={{ bold: <strong /> }}
          values={{
            participantEmail: user.email,
          }}
        />,
        { type: ToastType.success },
      );
    } catch (error) {
      showToast(t('certificates.toast.error'), {
        type: ToastType.error,
      });
      console.log('Error getting certificate: ', error);
    }

    setLoading(false);
  }, [certificateId, user.email, issueCert]);

  return (
    <TextButton
      className={classes.text}
      onClick={onClick}
      priority={TextButtonPriority.link}
    >
      {loading ? (
        <ThreeDotsLoader />
      ) : (
        t('certificates.button.view-certificate')
      )}
    </TextButton>
  );
};
